/**
 * StoneOrb — the atomic building block of YUO's material.
 *
 *  <StoneOrb color="mirror" size={22} facets dust darkHalo />
 *
 * Sizes:
 *   12 — header title dot
 *   16–22 — inline / nav
 *   26–38 — inside cards
 *   44–50 — send button
 *   64–88 — hero / big stones
 *
 * When facets=true a slow-spinning conic-gradient wedge overlays the stone;
 * when dust=true five tiny star pixels breathe on top;
 * when darkHalo=true the stone is wrapped in a soft black smoke layer that
 * kills any white-ring illusion around bright cores.
 */
import { STONE_GRADIENTS, ROOMS } from "@/lib/yuoDesign";

export default function StoneOrb({
  color = "mirror",
  size = 22,
  facets = false,
  dust = false,
  darkHalo = false,
  style,
  title,
  ...rest
}) {
  const meta = ROOMS[color] || ROOMS.mirror;
  const grad = STONE_GRADIENTS[color] || STONE_GRADIENTS.mirror;
  const outerAlpha = size >= 44 ? 0.35 : 0.22;

  return (
    <div
      title={title}
      style={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        ...style,
      }}
      {...rest}
    >
      {darkHalo && (
        <div style={{
          position: "absolute", inset: "-210%", borderRadius: "50%", pointerEvents: "none",
          background:
            "radial-gradient(circle, rgba(52,255,150,0.10) 0%, rgba(3,6,12,0.55) 34%, rgba(5,6,12,0) 72%)",
        }} />
      )}
      <div style={{
        position: "relative",
        width: size, height: size, borderRadius: "50%",
        background: grad,
        boxShadow:
          `0 0 ${size * 2}px rgba(${meta.rgb},${outerAlpha}),` +
          `inset -${Math.max(4, size * 0.14)}px -${Math.max(5, size * 0.17)}px ${Math.max(10, size * 0.4)}px rgba(0,0,0,0.9),` +
          `inset ${Math.max(3, size * 0.1)}px ${Math.max(3, size * 0.1)}px ${Math.max(8, size * 0.25)}px rgba(255,255,255,0.22)` +
          (darkHalo ? `,0 0 ${Math.max(60, size * 1.5)}px ${Math.max(20, size * 0.5)}px rgba(2,4,8,0.9)` : ""),
      }}>
        {facets && (
          <div style={{
            position: "absolute", inset: 2, borderRadius: "50%",
            background:
              "conic-gradient(transparent 0deg, rgba(255,255,255,0.5) 12deg, transparent 26deg," +
              " transparent 120deg, rgba(180,255,220,0.4) 132deg, transparent 148deg," +
              " transparent 250deg, rgba(255,255,255,0.35) 262deg, transparent 276deg)",
            mixBlendMode: "screen",
            animation: "yuo-spin 22s linear infinite",
          }} />
        )}
        {dust && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            background:
              "radial-gradient(1.5px 1.5px at 24% 62%, rgba(255,255,255,0.95), transparent)," +
              "radial-gradient(1.5px 1.5px at 68% 30%, rgba(255,255,255,0.9), transparent)," +
              "radial-gradient(1.5px 1.5px at 52% 76%, rgba(200,255,225,0.9), transparent)," +
              "radial-gradient(1px 1px at 78% 58%, rgba(255,255,255,0.8), transparent)," +
              "radial-gradient(1px 1px at 38% 40%, rgba(255,255,255,0.7), transparent)",
            animation: "yuo-breathe 6s ease-in-out infinite",
          }} />
        )}
      </div>
    </div>
  );
}
